import {
  QButton,
  QCloseButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QText,
  useToastProvider,
  useCurrentUser as useQCurrentUser,
} from '@qualio/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { ModalComponentProps, UpdateUserByIdRequestBody, User } from '../../types';
import { createErrorToast, createSuccessToast } from '../../utils/toast.utils';
import {
  isBillingUser,
  isUserChangingSelf,
  isUserCompanyAdmin,
  isUserInvitePending,
  isValidFullName,
} from '../../utils/users.utils';
import { validateEmail } from '../../utils/validation/email';
import UserInfo, { UserInfoFormFields } from '../../components/FormComponents/UserInfo';
import { ErrorCodes, getErrorMessageOrDefault, getErrorResponseMessageOrDefault } from '../../errors/um-errors';
import redirect from '../../utils/redirect';
import { updateUser } from '../../api/medtech-api';

type UserModalProps = ModalComponentProps & {
  companyId: number;
  user: User;
  getUsers: () => Promise<void>;
};

type FormValues = UserInfoFormFields;

export const UserModal: React.FC<UserModalProps> = ({ closeModal, companyId, user, getUsers }) => {
  const { userId: currentUserId } = useQCurrentUser();
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const inviteIsPending = isUserInvitePending(user);
  const initialValues: FormValues = {
    fullName: user.full_name,
    email: user.email,
    role: user.role,
    adminAccess: user.isAdmin,
    billingAccess: isBillingUser(user),
  };

  // formik form validator (updating once Heechul's branch is merged)
  const formValidation = (values: FormValues) => {
    const errors: Partial<FormValues> = {};

    if (inviteIsPending) {
      return errors;
    }

    if (!isValidFullName(values.fullName)) {
      errors.fullName = t('messages.validation.required');
    }

    if (!validateEmail(values.email)) {
      errors.email = t('messages.validation.invalidEmail');
    }

    return errors;
  };

  const sendUserUpdate = async (user_id: number, payload: UpdateUserByIdRequestBody) => {
    return updateUser(companyId, user_id, payload);
  };

  const handleSave = async (values: FormValues) => {
    const payload = {
      fullName: values.fullName,
      email: values.email,
      role: values.role,
      isBilling: values.billingAccess,
      is_admin: values.adminAccess,
    };

    try {
      const updatedUser = await sendUserUpdate(user.id, payload);

      if (!isUserCompanyAdmin(updatedUser, companyId) && isUserChangingSelf(currentUserId, user.id)) {
        redirect();
      }

      await getUsers();
      showToast(createSuccessToast(t('messages.success.userUpdated'), 'user-update-success-toast'));
      closeModal();
    } catch (err: unknown) {
      const errorMessageMap = {
        [ErrorCodes.MINIMUM_ADMINS_REQUIRED]: t('messages.validation.minimumAdmins'),
        [ErrorCodes.MINIMUM_QM_USER_REQUIRED]: t('messages.validation.minimumQuality'),
        [ErrorCodes.INVALID_USER_UPDATE]: getErrorResponseMessageOrDefault(err, t('messages.error.updateUser')),
        [ErrorCodes.ACTIVE_USERS_LIMIT_REACHED]: t('messages.validation.activeUsersLimit'),
        [ErrorCodes.EMAIL_ALREADY_EXISTS]: t('messages.validation.userExistsInQualio'),
      };

      const errorMessage = getErrorMessageOrDefault(err, errorMessageMap, t('messages.error.updateUser'));
      showToast(createErrorToast(errorMessage, 'user-update-error-toast'));
    }
  };

  return (
    <Formik initialValues={initialValues} validate={formValidation} onSubmit={handleSave}>
      {({ dirty, isValid, isSubmitting, submitForm }) => {
        // save button should be disabled unless the form has changed and is valid
        const saveDisabled = !dirty || !isValid;

        return (
          <Form>
            <QModal onClose={closeModal} size="2xl" isOpen>
              <QModalHeader>
                <QText>{t('modals.editUser.title')}</QText>
                <QCloseButton onClick={closeModal} />
              </QModalHeader>
              <QModalBody>
                <UserInfo inviteIsPending={inviteIsPending} />
              </QModalBody>
              <QModalActions>
                <QButton variant="outline" onClick={closeModal}>
                  {t('common.cancel')}
                </QButton>
                <QButton onClick={submitForm} isLoading={isSubmitting} isDisabled={saveDisabled}>
                  {t('common.save')}
                </QButton>
              </QModalActions>
            </QModal>
          </Form>
        );
      }}
    </Formik>
  );
};
